import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../lib/contexts/AuthContext';
import { authService } from '../../lib/services/auth';
import { DynamicTitle } from '../DynamicTitle';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';
import { isComensalRegistroIncompleto } from '../../lib/utils/comensalProfileCompleteness';
import {
  listPropuestasFavoritasPorUsuario,
  type PropuestaFavoritaResumen,
} from '../../lib/services/propuestas-favoritas';
import { countTokensForUser } from '../../lib/services/app-push';

const DELETE_CONFIRM_MSG =
  'Se eliminará tu cuenta de forma permanente: perfil, sesión y datos vinculados en la medida que permita el sistema. Esta acción no se puede deshacer.\n\n¿Continuar?';

export function ComensalPerfilPage() {
  const navigate = useNavigate();
  const { signOut, user, profile } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const registroIncompleto = isComensalRegistroIncompleto(profile);

  const [favoritas, setFavoritas] = useState<PropuestaFavoritaResumen[]>([]);
  const [favoritasLoading, setFavoritasLoading] = useState(false);
  const [pushActivas, setPushActivas] = useState<boolean | null>(null);
  const [pushLoading, setPushLoading] = useState(false);

  const cargarPreferencias = useCallback(async () => {
    if (!user?.id) {
      setFavoritas([]);
      setPushActivas(null);
      return;
    }
    setFavoritasLoading(true);
    setPushLoading(true);
    try {
      const [favs, tokens] = await Promise.all([
        listPropuestasFavoritasPorUsuario(user.id).catch(() => [] as PropuestaFavoritaResumen[]),
        countTokensForUser(user.id).catch(() => 0),
      ]);
      setFavoritas(favs);
      setPushActivas(tokens > 0);
    } finally {
      setFavoritasLoading(false);
      setPushLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void cargarPreferencias();
  }, [cargarPreferencias]);

  const showError = (msg: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.alert(msg);
    } else {
      Alert.alert('Error', msg);
    }
  };

  const handleSalir = async () => {
    await signOut();
    navigate('/entrar', { replace: true });
  };

  const runDeleteAccount = async () => {
    if (!user?.id || deleting) return;
    setDeleting(true);
    try {
      await authService.deleteUser(user.id);
    } catch (err: any) {
      const msg = err?.message ?? 'No se pudo eliminar la cuenta. Intenta de nuevo más tarde.';
      showError(msg);
      setDeleting(false);
      return;
    }
    try {
      await signOut();
    } catch {
      /* sesión ya invalidada en servidor */
    }
    setDeleting(false);
    navigate('/entrar', { replace: true });
  };

  const handleEliminarCuenta = () => {
    if (!user?.id || deleting) return;

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      if (!window.confirm(DELETE_CONFIRM_MSG)) return;
      void runDeleteAccount();
      return;
    }

    Alert.alert('Eliminar cuenta', DELETE_CONFIRM_MSG, [
      { text: 'Cancelar', style: 'cancel' },
      { text: 'Eliminar cuenta', style: 'destructive', onPress: () => void runDeleteAccount() },
    ]);
  };

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      <DynamicTitle title="Perfil — As Gastronómico" />
      <Text style={styles.title}>Perfil</Text>
      <Text style={styles.sub}>Datos de tu cuenta y preferencias.</Text>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>Notificaciones</Text>
        {pushLoading ? (
          <ActivityIndicator color="#A20000" style={styles.cardSpinner} />
        ) : (
          <View style={styles.statusRow}>
            <View
              style={[
                styles.statusDot,
                { backgroundColor: pushActivas ? '#16a34a' : '#9ca3af' },
              ]}
            />
            <Text style={styles.statusText}>
              {pushActivas ? 'Activadas en este dispositivo' : 'Desactivadas'}
            </Text>
          </View>
        )}
      </View>

      <View style={styles.card}>
        <View style={styles.cardTitleRow}>
          <Text style={styles.cardTitle}>Propuestas favoritas</Text>
          <TouchableOpacity onPress={() => navigate('/likes')} hitSlop={8} activeOpacity={0.7}>
            <Text style={styles.cardLink}>Ver todas</Text>
          </TouchableOpacity>
        </View>
        {favoritasLoading ? (
          <ActivityIndicator color="#A20000" style={styles.cardSpinner} />
        ) : favoritas.length === 0 ? (
          <Text style={styles.emptyText}>Aún no tienes propuestas favoritas.</Text>
        ) : (
          favoritas.slice(0, 5).map((fav) => (
            <View key={fav.id} style={styles.favRow}>
              <Ionicons name="heart" size={16} color="#A20000" />
              <View style={styles.favBody}>
                <Text style={styles.favTitulo} numberOfLines={2}>
                  {fav.titulo}
                </Text>
                <Text style={styles.favMeta} numberOfLines={1}>
                  {[fav.restauranteNombre, fav.edicionNombre].filter(Boolean).join(' · ') || '—'}
                </Text>
              </View>
            </View>
          ))
        )}
      </View>

      {registroIncompleto ? (
        <TouchableOpacity
          style={styles.outlineButton}
          onPress={() => navigate('/completar-perfil')}
          activeOpacity={0.8}
          disabled={deleting}
        >
          <Ionicons name="person-outline" size={20} color="#030213" style={styles.outlineIcon} />
          <Text style={styles.outlineText}>Completar registro</Text>
        </TouchableOpacity>
      ) : null}

      <TouchableOpacity
        style={styles.outlineButton}
        onPress={handleSalir}
        activeOpacity={0.8}
        disabled={deleting}
      >
        <Ionicons name="log-out-outline" size={20} color="#030213" style={styles.outlineIcon} />
        <Text style={styles.outlineText}>Cerrar sesión</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.dangerButton, deleting && styles.dangerButtonDisabled]}
        onPress={handleEliminarCuenta}
        activeOpacity={0.8}
        disabled={deleting}
      >
        {deleting ? (
          <ActivityIndicator color="#A20000" />
        ) : (
          <>
            <Ionicons name="trash-outline" size={20} color="#A20000" style={styles.outlineIcon} />
            <Text style={styles.dangerButtonText}>Eliminar mi cuenta</Text>
          </>
        )}
      </TouchableOpacity>

      <Text style={styles.dangerHint}>
        Al eliminar la cuenta perderás el acceso y se borrarán los datos asociados a tu usuario según la política del
        servicio.
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 32 + COMENSAL_TAB_BAR_OVERLAY_INSET,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#030213',
    marginBottom: 8,
  },
  sub: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
    marginBottom: 20,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    marginBottom: 14,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#030213',
  },
  cardLink: {
    fontSize: 13,
    fontWeight: '600',
    color: '#A20000',
  },
  cardSpinner: {
    marginVertical: 8,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151',
  },
  emptyText: {
    marginTop: 4,
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
  },
  favRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#e5e7eb',
  },
  favBody: {
    flex: 1,
    minWidth: 0,
  },
  favTitulo: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  favMeta: {
    marginTop: 2,
    fontSize: 12,
    color: '#6b7280',
  },
  outlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 10,
    paddingVertical: 14,
    backgroundColor: '#ffffff',
    marginBottom: 14,
  },
  outlineIcon: {
    marginRight: 8,
  },
  outlineText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#030213',
  },
  dangerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 10,
    paddingVertical: 14,
    backgroundColor: '#fef2f2',
  },
  dangerButtonDisabled: {
    opacity: 0.7,
  },
  dangerButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#A20000',
  },
  dangerHint: {
    marginTop: 12,
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 18,
  },
});
